import React, { memo, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { toCamelCase } from ".";
import { useAppSelector } from "../../Store";
import { Graph_QL, useAnimal, useOldSection, useMilestones, Consumption, AnimalInfo } from "../../Services";
import { MilestoneInfo } from "../../Services/Agrisys_GqlDataCaller/MilestoneDataCalls/GQLCalls/getMilestonesCall";
import ProgressCircle from "../ProgressViewer/progressCircle";
import { UNIT } from "..";
import { colors, glyphs } from "../../Styles";
import { CircleTypes } from "../ProgressViewer/_ProgressViewer/progressDefinitions";
import { useAnimalConsumptionByMultipleIds } from "../../Services/Agrisys_GqlDataCaller/ConsumptionCalls/consumptionSlice";
import { Animal } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";
import { useMultipleConsumptions } from "./consumption";
import { TFunction } from "i18next";
import { isEqual } from "lodash";


type ProgressCircleConst = {
  progressCircleId: number;
  header: string;
  unit: JSX.Element;
  tooMuchFedDisclaimer: boolean;
  circleType: CircleTypes;
}

// Progress circles on feeding
export type NewDefaultProgressCircle = {
  progressed: number;
  total: number;
} & ProgressCircleConst;

interface DefaultProgressCirclesProps {
  animals: Animal[];
  connect: {
    connection: Graph_QL.Connection
    domain: string
  }
}

// #region | Feed Consumption
const FEED_BALANCE = 0.2; //TODO get from settings?

const notFedLastDays = (allConsumptions: Consumption[][], feedBalance: number = FEED_BALANCE): { total: number, missing: number } => {
  let animalCnt = 0;
  let missingCnt = allConsumptions.filter((consumptions: Consumption[]) => {
    let const_Today = consumptions.find(e => e.milestoneDayOffset !== undefined && e.milestoneDayOffset === 0)
    let const_Yesterday = consumptions.find(e => e.milestoneDayOffset !== undefined && e.milestoneDayOffset === -1)


    if (!const_Today || !const_Yesterday) return false

    animalCnt++;
    let MissingToday = (const_Today.progressed ?? 0) < (const_Today.total ?? 0) * (1 - feedBalance)
    let MissingYesterday = (const_Yesterday.progressed ?? 0) < (const_Yesterday.total ?? 0) * (1 - feedBalance)
    return MissingToday && MissingYesterday
  }).length;
  return { total: animalCnt, missing: missingCnt }
}

export const underFeedBalanceYesterday = (allConsumptions: (Consumption[] | undefined)[], feedBalance: number = FEED_BALANCE): number => {

  const underFeedBalanceArr = allConsumptions.filter((consumptions) => {
    if (consumptions === undefined) {
      return false;
    }

    const consumptionsYesterday = consumptions.filter((consumption) => consumption.milestoneDayOffset === -1 && consumption.componentId !== 1);

    let progressedSum = 0;
    let totalSum = 0;
    for (const consumption of consumptionsYesterday) {
      if (consumption.progressed) {
        progressedSum += consumption.progressed;
      }
      if (consumption.total) {
        totalSum += consumption.total;
      }
    }

    if (totalSum === 0) {
      return false;
    }

    return ((totalSum - progressedSum) / totalSum) * 100 > feedBalance;
  });

  const underFeedBalance = underFeedBalanceArr.length;

  return underFeedBalance;
}
// #endregion

const CIRCLE_IDS = {
  'remainingToday': 1,
  'remainingYesterday': 2,
  'waterUsedToday': 3,
  'notFed': 4,
  'milestone': 5
} as const;

type CircleId = typeof CIRCLE_IDS[keyof typeof CIRCLE_IDS];
type NonMilestoneCircleId = Exclude<CircleId, typeof CIRCLE_IDS['milestone']>;


function progressCircleConst(t: TFunction, circleId: typeof CIRCLE_IDS['milestone'], milestone: { name: string, milestone_id: number, index: number }): ProgressCircleConst
function progressCircleConst(t: TFunction, circleId: NonMilestoneCircleId): ProgressCircleConst
function progressCircleConst(t: TFunction, circleId: CircleId, milestone?: { name: string, milestone_id: number, index: number }): ProgressCircleConst {
  switch (circleId) {
    default:
    case CIRCLE_IDS.remainingToday: {
      return {
        progressCircleId: circleId,
        header: t("feed.general.remainingToday", {
          defaultValue: "remaining today",
        }),
        unit: <UNIT content={"Kg"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary} />} />,
        tooMuchFedDisclaimer: true,
        circleType: CircleTypes.feed
      }
    }
    case CIRCLE_IDS.remainingYesterday: {
      return {
        progressCircleId: circleId,
        header: t("feed.general.remainingYesterday", {
          defaultValue: "remaining yesterday",
        }),
        unit: <UNIT content={"Kg"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary} />} />,
        tooMuchFedDisclaimer: true,
        circleType: CircleTypes.feed
      }
    }
    case CIRCLE_IDS.waterUsedToday: {
      return {
        progressCircleId: circleId,
        header: t("feed.general.waterUseToday", { defaultValue: "Water use today" }),
        unit: <UNIT content={"Kg"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary} />} />,
        tooMuchFedDisclaimer: false,
        circleType: CircleTypes.feed
      }
    }
    case CIRCLE_IDS.notFed: {
      return {
        progressCircleId: circleId,
        header: t("feed.general.notFed", { defaultValue: "fed <20% last two days" }),
        unit: <UNIT content={"Animals"} color={colors.greyPrimary} />,
        tooMuchFedDisclaimer: false,
        circleType: CircleTypes.primary
      }
    }
    case CIRCLE_IDS.milestone: {
      const milestoneKey = toCamelCase(milestone!.name);
      return {
        progressCircleId: circleId + milestone!.index,
        header: t(`milestones.defaultMilestones.${milestoneKey}`, {
          defaultValue: milestone!.name,
        }),
        unit: <UNIT content={"Animals"} color={colors.greyPrimary} />,
        tooMuchFedDisclaimer: false,
        circleType: CircleTypes.primary
      };
    }
  }
}


const useDefaultProgressCirclesData = ({ animals, connect }: DefaultProgressCirclesProps): NewDefaultProgressCircle[] => {

  const { t } = useTranslation();

  const { call: getMilestoneInfo, data: milestonesState } = useMilestones(connect.connection);

  useEffect(() => {
    getMilestoneInfo({ fetchAmount: 50 });
  }, []);

  const consumptionEntities = useAnimalConsumptionByMultipleIds(animals.map(a => a.animal_id.toString()))
  const consumptionsArray = Object.values(consumptionEntities).filter((entity) => !entity.isLoading && entity.entity !== undefined).map((entityConsumption) => entityConsumption.entity?.consumptions)
  const notFeed = useMemo(() => notFedLastDays(consumptionsArray.filter(c => c !== undefined)), [consumptionsArray])

  const consumptionData = useMultipleConsumptions(animals.map(a => a.animal_id));

  // #region | Milestones
  const milestones = useMemo(() => {
    let result: MilestoneInfo[] = [];
    if (milestonesState && milestonesState[connect.domain]) {
      result = milestonesState[connect.domain].data;
    }
    return result;
  }, [connect.domain, milestonesState])
  // #endregion

  const calculatedProgressCircles = useMemo(() => {
    let result: NewDefaultProgressCircle[] = []



    let baseCircles: NewDefaultProgressCircle[] = [
      {
        ...progressCircleConst(t, CIRCLE_IDS.remainingToday),
        progressed: +(consumptionData[0].feed.remaning),
        total: +consumptionData[0].feed.total,
      },
      {
        ...progressCircleConst(t, CIRCLE_IDS.remainingYesterday),
        progressed: +(consumptionData[1].feed.remaning),
        total: +consumptionData[1].feed.total,
      },
      {
        ...progressCircleConst(t, CIRCLE_IDS.waterUsedToday),
        progressed: +(consumptionData[0].water.remaning),
        total: +(consumptionData[0].water.total),
      },
      {
        ...progressCircleConst(t, CIRCLE_IDS.notFed),
        progressed: notFeed.missing,
        total: notFeed.total,
      },
    ]
    result = result.concat(baseCircles)

    let milestoneCircles: NewDefaultProgressCircle[] = milestones.map((milestone, index) => {
      return {
        ...progressCircleConst(t, 5, { ...milestone, index }),
        progressed:
          animals.filter(
            (animal: Animal) =>
              animal.milestone?.milestone_id === milestone.milestone_id
          ).length ?? 0,
        total: animals.length,
      };
    })
    result = result.concat(milestoneCircles)
    return result
  }, [animals, consumptionData, milestones, notFeed.missing, notFeed.total, t])

  return calculatedProgressCircles;
};

export default useDefaultProgressCirclesData;

export const NewProgressCicle = memo(({ animals, circleId, connect }: { animals: Animal[], circleId: number, connect: { connection: Graph_QL.Connection, domain: string } }) => {
  const defaultProgressCircles = useDefaultProgressCirclesData({ animals, connect });
  const circleData = defaultProgressCircles.find(c => c.progressCircleId === circleId)
  if (!circleData) return;

  return (
    <ProgressCircle
      key={circleData.progressCircleId}
      progressed={circleData.progressed}
      total={circleData.total}
      circleType={circleData.circleType}
      animation
      labels={{
        header: circleData.header,
        unit: circleData.unit,
      }}
      extension={{
        hasExtension: circleData.tooMuchFedDisclaimer
      }}
    />
  )
});

export const NonMilstoneProgressCircleWithDataProp = memo(({ circleId, progressed, total }: { circleId: NonMilestoneCircleId, progressed: number, total: number }) => {
  const { t } = useTranslation();
  const { circleType, header, unit, tooMuchFedDisclaimer } = progressCircleConst(t, circleId);

  return (<ProgressCircle
    key={circleId}
    progressed={progressed}
    total={total}
    circleType={circleType}
    animation
    labels={{
      header,
      unit
    }}
    extension={{
      hasExtension: tooMuchFedDisclaimer
    }}
  />)
}) 
